import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SEARCH_PARAMS } from '../constants/searchParamNames';
import {
  logIn,
  getUserConsents,
  pingHelloWorld,
  userHasNalogCookie,
  persistUserConsentsInLocalStorage,
  shouldRedirectToPrivacySettings,
  getAndStoreUserDataBySession
} from '../utils/auth';
import { useEffect } from 'react';
import { LOGIN_REDIRECT } from '../constants/config';
import LoadingIndicator from './LoadingIndicator';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys.ts';

interface Props {
  setIsAuthorizing: React.Dispatch<React.SetStateAction<boolean>>;
}

const privacySettingsPage = '/profil/privatnost';

const Authorization: React.FC<Props> = ({ setIsAuthorizing }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirected = searchParams.get(SEARCH_PARAMS.REDIRECTED) === 'true';
  const hasNalogCookie = userHasNalogCookie();

  const redirectPathname = new URL(LOGIN_REDIRECT).pathname;

  // Utilities.
  const getStoreAndCheckConsents = async () => {
    let failureStatus: number | undefined;
    const response = await getUserConsents({
      onError: info => (failureStatus = info.status)
    });

    // A 403 may arrive as a real HTTP 403 (failureStatus) or as a 2xx envelope
    // carrying statusCode 403 — handle both, and restart the fresh flow.
    if (failureStatus === 403 || response?.statusCode === 403) {
      initFreshAuthorizationFlow();
      return;
    }

    if (!response) {
      navigate('/greska-prilikom-autorizacije');
      return;
    }

    if (response.session?.avatarUrl) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.AVATAR_URL,
        response.session.avatarUrl
      );
    }

    persistUserConsentsInLocalStorage(response.data);

    const shouldRedirect = shouldRedirectToPrivacySettings();

    // Eject from here and go to privacy settings.
    if (shouldRedirect && location.pathname !== privacySettingsPage) {
      navigate(`/profil/privatnost?callback=${location.pathname}`);
    }

    return;
  };

  // 1. Start a fresh flow which may end up going to the login redirect page and then back here again.
  const initFreshAuthorizationFlow = async () => {
    try {
      await pingHelloWorld().then(async response => {
        if (response.session === null) {
          // Start the login process (go to Nalog and then back to the login redirect page).
          logIn();
        } else {
          // The user is logged in, fetch consents and user data.
          await getStoreAndCheckConsents();
          await getAndStoreUserDataBySession();

          // END OF FLOW.
          setIsAuthorizing(false);
        }
      });
    } catch (e) {
      // If an error occurs and the user has a cookie, try to log him in "the long way round".
      console.warn(
        'Autorizacioni flow nije uspeo, pokušavam ponovni login:',
        e
      );
      logIn();
    }
  };

  // 2. Handle user that just got here from the login redirect page.
  const getConsentsAndCleanUpSearchParams = async () => {
    try {
      await getStoreAndCheckConsents();

      searchParams.delete(SEARCH_PARAMS.REDIRECTED);
      setSearchParams(searchParams);

      // END OF FLOW.
      setIsAuthorizing(false);
    } catch (e) {
      console.warn(
        'Greška pri dohvatu saglasnosti, preusmeravam na privatnost:',
        e
      );
      if (location.pathname !== privacySettingsPage) {
        navigate(`/profil/privatnost?callback=${location.pathname}`);
      } else {
        // Drop out fully.
        setIsAuthorizing(false);
        navigate('/');
      }
    }
  };

  // Main login logic.
  useEffect(() => {
    // Don't do anything on the login redirect page or the privacy settings page.
    if (location.pathname === redirectPathname) {
      return;
    }

    if (!hasNalogCookie) {
      // END OF FLOW.
      setIsAuthorizing(false);
    } else {
      if (redirected) {
        // 2. Handle user that just got here from the login redirect page.
        getConsentsAndCleanUpSearchParams();
      } else {
        // 1. Start a fresh flow which may end up going to the login redirect page and then back here again.
        initFreshAuthorizationFlow();
      }
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <div className="d-flex justify-content-center align-items-center full-height">
      <LoadingIndicator />
    </div>
  );
};

export default Authorization;
